import { Button, ErrorAlert, Notice, SelectInput, Spinner, linkStyles } from '@lumlab/platform-ui';
import { apiRequest } from '@lumlab/platform-product/web';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { AuthScreen } from '../app/AuthScreen.tsx';
import { useRegistrationConfig } from '../app/account.ts';

export function OnboardingPage() {
    const { t } = useTranslation();
    const config = useRegistrationConfig();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [productId, setProductId] = useState('');
    const [verticalId, setVerticalId] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const product = config.data?.catalog.find((candidate) => candidate.id === productId);
    return (
        <AuthScreen title={t('platform.onboarding.title')}>
            <p className="text-sm text-muted">{t('platform.onboarding.intro')}</p>
            {config.isPending && <Spinner label={t('platform.shell.loading')} />}
            {config.error && <ErrorAlert error={config.error} />}
            {error !== null && <ErrorAlert error={error} />}
            {config.data && (
                <form
                    className="flex flex-col gap-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        setPending(true);
                        setError(null);
                        void (async () => {
                            try {
                                await apiRequest('/api/account/onboarding', {
                                    method: 'POST',
                                    body: { productId, verticalId },
                                });
                                await queryClient.invalidateQueries({ queryKey: ['account-state'] });
                                await navigate('/', { replace: true });
                            } catch (failure) {
                                setError(failure);
                            } finally {
                                setPending(false);
                            }
                        })();
                    }}
                >
                    <SelectInput
                        label={t('platform.onboarding.service')}
                        value={productId}
                        required
                        onChange={(event) => {
                            setProductId(event.target.value);
                            setVerticalId('');
                        }}
                    >
                        <option value="">{t('platform.onboarding.choose')}</option>
                        {config.data.catalog.map((entry) => (
                            <option key={entry.id} value={entry.id}>
                                {t(entry.nameKey)}
                            </option>
                        ))}
                    </SelectInput>
                    <SelectInput
                        label={t('platform.onboarding.vertical')}
                        value={verticalId}
                        required
                        disabled={!product}
                        onChange={(event) => setVerticalId(event.target.value)}
                    >
                        <option value="">{t('platform.onboarding.choose')}</option>
                        {product?.verticals.map((vertical) => (
                            <option key={vertical.id} value={vertical.id}>
                                {t(vertical.nameKey)}
                            </option>
                        ))}
                    </SelectInput>
                    <Notice tone="info">{t('platform.onboarding.nextSteps')}</Notice>
                    <Button type="submit" loading={pending} disabled={!productId || !verticalId}>
                        {t('platform.onboarding.submit')}
                    </Button>
                </form>
            )}
            <Link to="/security" className={`text-sm ${linkStyles}`}>
                {t('platform.security.title')}
            </Link>
        </AuthScreen>
    );
}
